import type { TeamWith } from "@redotech/account-common/base-team";
import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { GetUser } from "@redotech/account-common/user";
import { ReviewingParty } from "@redotech/congo-enums/reviewing-party";
import { UpsellBillingTrigger } from "@redotech/congo-enums/upsell-billing-trigger";
import { getCurrencyFormatter } from "@redotech/money/util";
import { ShipmentTrackerStatus } from "@redotech/redo-fulfillment-group-common/outbound-shipment-label";
import { t } from "@redotech/redo-i18n";
import { providerSupportsAdvancedExchanges } from "@redotech/redo-order-common/provider";
import type { PackageProtectionSettings } from "@redotech/redo-package-protection-settings/settings";
import {
  MerchantAppReturn,
  Product,
  ProductZod,
  Return,
  ReturnType,
} from "@redotech/redo-return-core-common/return";
import {
  isClosedReturnStatus,
  ReturnStatus,
} from "@redotech/redo-return-core-common/return-status";
import { ReturnedProductStatus } from "@redotech/redo-return-core-common/returned-product-status";
import { isVariantExchangeProduct } from "@redotech/redo-return-core-common/returns/exchange-items";
import { ReturnTotalsType } from "@redotech/redo-return-core-common/returns/return-totals/return-totals-set";
import { SerializedReturnSummaryTotals } from "@redotech/redo-return-core-common/returns/return-totals/types";
import { RedoBadgeColor } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { formatDate } from "@redotech/redo-web/time";
import { assertNever } from "@redotech/util/type";

export function formatDateInMerchantTimeZone(
  date: Temporal.Instant,
  timezone: string,
): string {
  if (!timezone) {
    return formatDate(date);
  }

  const merchantZonedTime = date.toZonedDateTimeISO(timezone);

  return formatDate(merchantZonedTime);
}

export function getPresentmentCurrencyForReturn(return_: MerchantAppReturn) {
  return return_.presentmentCurrency ?? return_.currency;
}

export function getPresentmentCurrencyLabelForReturn(
  return_: MerchantAppReturn,
) {
  return getPresentmentCurrencyForReturn(return_) ?? t("order currency");
}

export function answerName(answer: string) {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return answer[0].toUpperCase() + answer.slice(1);
}

export function shipmentStatusName(status: string) {
  if (status === ReturnStatus.DELETED) {
    return t("Reset");
  }
  if (!status) {
    return "-";
  }
  // EasyPost confirmed that "pre_transit" and "unknown" are interchangeable and can be treated as the same
  if (status === "pre_transit" || status === "unknown") {
    return "Pre-transit";
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return status[0].toUpperCase() + status.slice(1).replace(/_/g, " ");
}

export function returnStatusName(status: ReturnStatus) {
  if (status === ReturnStatus.DELETED) {
    return t("Reset");
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return status[0].toUpperCase() + status.slice(1).replace(/_/g, " ");
}

export function returnKindNameProduct(
  product: ProductZod | Product,
  provision?: string,
  returnType?: string,
): string {
  const isExchange = isVariantExchangeProduct(product);
  if (returnType && returnType === "claim" && isExchange) {
    return "Replace";
  }
  if (isExchange) {
    return "Exchange";
  }
  return (
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    product.strategy[0].toUpperCase() +
    product.strategy.slice(1).replace(/_/g, " ")
  );
}

export function returnKindNameReturn(
  return_: Return | MerchantAppReturn,
  abbreviate = true,
) {
  if (return_?.advancedExchangeItems?.length) {
    if (return_.provision === "instant") {
      return "Instant Exchange";
    } else {
      return "Exchange";
    }
  }
  const uniqueTypes = new Set(
    return_.products.map((product) =>
      returnKindNameProduct(product, return_.provision, return_.type),
    ),
  );
  const types = [...uniqueTypes].map((returnType) => {
    if (returnType === "Exchange" && return_.provision === "instant") {
      return "Instant Exchange";
    } else {
      return returnType;
    }
  });

  if (abbreviate) {
    let [type] = types.sort();
    if (1 < types.length) {
      type += " …";
    }
    return type;
  } else {
    return types.sort().join(", ");
  }
}

export function getReturnTypes(
  return_: Pick<
    Return,
    "advancedExchangeItems" | "provision" | "products" | "type"
  >,
): string[] {
  if (return_?.advancedExchangeItems?.length) {
    return [return_.provision === "instant" ? "Instant Exchange" : "Exchange"];
  }
  const uniqueTypes = new Set(
    return_.products.map((product) =>
      returnKindNameProduct(product, return_.provision, return_.type),
    ),
  );
  return [...uniqueTypes]
    .map((returnType) =>
      returnType === "Exchange" && return_.provision === "instant"
        ? "Instant Exchange"
        : returnType,
    )
    .sort();
}

export type ItemDispositionModalOpen =
  | { open: true; item: ProductZod }
  | { open: false };

export const returnStatusToBadgeColor: Record<ReturnStatus, RedoBadgeColor> = {
  [ReturnStatus.COMPLETE]: "success",
  [ReturnStatus.DELIVERED]: "blue",
  [ReturnStatus.IN_TRANSIT]: "pink",
  [ReturnStatus.NEEDS_REVIEW]: "blue",
  [ReturnStatus.IN_REVIEW]: "orange",
  [ReturnStatus.OPEN]: "warning",
  [ReturnStatus.REJECTED]: "error",
  [ReturnStatus.DELETED]: "warning",
  [ReturnStatus.FLAGGED]: "orange",
  [ReturnStatus.PRE_SHIPMENT]: "blueLight",
};

export const getReturnId = (return_: { _id: string }): string => return_._id;

export function returnStatusLabel(
  status: ReturnStatus,
  t: (key: string) => string,
) {
  switch (status) {
    case ReturnStatus.COMPLETE:
      return t("Complete");
    case ReturnStatus.DELIVERED:
      return t("Delivered");
    case ReturnStatus.IN_TRANSIT:
      return t("In transit");
    case ReturnStatus.NEEDS_REVIEW:
      return t("Needs review");
    case ReturnStatus.IN_REVIEW:
      return t("In review");
    case ReturnStatus.OPEN:
      return t("Open");
    case ReturnStatus.REJECTED:
      return t("Rejected");
    case ReturnStatus.FLAGGED:
      return t("Flagged");
    case ReturnStatus.PRE_SHIPMENT:
      return t("Pre-shipment");
    case ReturnStatus.DELETED:
      return t("Reset");
  }
}

export function itemStatusName(
  productStatus: ReturnedProductStatus,
  returnStatus: ReturnStatus,
) {
  if (returnStatus === ReturnStatus.DELETED) {
    return "Reset";
  }
  switch (productStatus) {
    case ReturnedProductStatus.COMPLETE:
      return "Processed";
    case ReturnedProductStatus.REJECTED:
      return "Rejected";
    case ReturnedProductStatus.PENDING:
      return "Pending";
    case ReturnedProductStatus.PRE_SHIPMENT:
      return "Pre-Shipment";
    case ReturnedProductStatus.APPROVED:
    case ReturnedProductStatus.IN_REVIEW:
    case ReturnedProductStatus.OPEN:
      return "Unprocessed";
    default:
      assertNever(productStatus);
  }
}

export function isExchangeOrderEditable(
  return_: MerchantAppReturn,
  { canEditReturn }: { canEditReturn: boolean },
) {
  const isProcessed = [ReturnStatus.COMPLETE, ReturnStatus.REJECTED].includes(
    return_.status,
  );
  const isReset = [ReturnStatus.DELETED].includes(return_.status);
  const exchangeOrderExists =
    !!return_.exchangeOrder?.length || !!return_.draftOrderId;

  return (
    canEditReturn &&
    !return_.instantRefund &&
    !isProcessed &&
    !isReset &&
    !exchangeOrderExists
  );
}

export function isAdvancedExchangeEditable(
  return_: MerchantAppReturn,
  permissions: { canEditReturn: boolean },
) {
  return (
    providerSupportsAdvancedExchanges(return_.provider) &&
    isExchangeOrderEditable(return_, permissions)
  );
}

export const returnTypeName = (returnType?: ReturnType, titleCase = false) =>
  titleCase ? (returnType === "return" ? t("Return") : t("Claim")) : returnType;

export function getProductsByShippingGroupTitle(return_: MerchantAppReturn) {
  const productsByGroupName: {
    groupName: string;
    shipmentNumbers: string;
    sortKey: number;
    products: ProductZod[];
  }[] = [];

  if (return_?.shipmentGroups?.length) {
    for (const product of return_.products) {
      const allGroups = return_.shipmentGroups;
      const shipmentGroupsIDs = product.shipmentGroupIDs?.length
        ? product.shipmentGroupIDs
        : product.shipmentGroupID
          ? [product.shipmentGroupID]
          : [];
      const shipmentGroups = return_.shipmentGroups.filter((group) =>
        shipmentGroupsIDs.includes(group.id),
      );
      const shipmentIndices: number[] = shipmentGroups
        .map((group) => allGroups.indexOf(group))
        .sort((a, b) => a - b);

      const shipmentNumbers = shipmentIndices.map((ind) => ind + 1).join(" + ");
      const groupName = `Shipment ${shipmentNumbers}`;
      const sortKey = shipmentIndices[0] ?? Number.MAX_SAFE_INTEGER;

      const productGroup = productsByGroupName.find(
        (group) => group.groupName === groupName,
      );
      if (productGroup) {
        productGroup?.products.push(product);
      } else {
        productsByGroupName.push({
          groupName,
          products: [product],
          shipmentNumbers,
          sortKey,
        });
      }
    }
  }

  return productsByGroupName
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ groupName, products, shipmentNumbers }) => ({
      groupName,
      products,
      shipmentNumbers,
    }));
}

/**
 * Returns true if the claim either owned by the merchant or the user has
 * manage managed claim permissions.
 */
export function showManagedClaimActions(
  return_?: MerchantAppReturn,
  user?: GetUser,
  team?: TeamWith<{ packageProtection?: PackageProtectionSettings }>,
) {
  if (!return_ || !user || !team) {
    return true;
  }
  if (!team.settings?.packageProtection?.newClaimsTables) {
    return true;
  }
  const isRedoManagedClaim = return_.reviewingParty === ReviewingParty.Redo;
  if (!isRedoManagedClaim) {
    return true;
  }
  const isManagedClaimAdmin = isRedoEmailAddress(user.email);
  return isRedoManagedClaim && isManagedClaimAdmin;
}

/**
 * Checks if a return has any shipments that are stuck in transit
 * Uses the same logic as the stuck in transit filter on the returns table:
 * 1. Return status must be one of: OPEN, IN_TRANSIT, DELIVERED, NEEDS_REVIEW, IN_REVIEW, FLAGGED
 * 2. Must have at least one shipment that:
 *    - Has a tracker status of IN_TRANSIT, OUT_FOR_DELIVERY, ERROR, or FAILURE
 *    - Has a last updated date more than 10 days ago
 */
export function isReturnStuckInTransit(return_: MerchantAppReturn): {
  isStuck: boolean;
  stuckShipmentCount: number;
} {
  if (!return_.shipments || return_.shipments.length === 0) {
    return { isStuck: false, stuckShipmentCount: 0 };
  }

  // First check if the return status matches the filter criteria
  const stuckReturnStatuses = [
    ReturnStatus.OPEN,
    ReturnStatus.IN_TRANSIT,
    ReturnStatus.DELIVERED,
    ReturnStatus.NEEDS_REVIEW,
    ReturnStatus.IN_REVIEW,
    ReturnStatus.FLAGGED,
  ];
  const hasStuckReturnStatus = stuckReturnStatuses.includes(return_.status);

  if (!hasStuckReturnStatus) {
    return { isStuck: false, stuckShipmentCount: 0 };
  }

  // Check if the shipment is in a stuck status
  const stuckShipmentStatuses = [
    ShipmentTrackerStatus.IN_TRANSIT,
    ShipmentTrackerStatus.OUT_FOR_DELIVERY,
    ShipmentTrackerStatus.ERROR,
    ShipmentTrackerStatus.FAILURE,
  ];

  let stuckShipmentCount = 0;

  // Check each shipment for stuck trackers
  for (const shipment of return_.shipments) {
    const tracker = shipment._shipment?.tracker;

    if (!tracker?.status) {
      continue;
    }

    const shipmentStatus = tracker.status;
    const lastUpdated = tracker.updated_at;

    if (
      stuckShipmentStatuses.includes(shipmentStatus as ShipmentTrackerStatus) &&
      lastUpdated
    ) {
      // Check if that shipment hasn't been updated in the last 10 days
      const lastUpdatedDate = new Date(lastUpdated);
      const tenDaysAgo = new Date();
      tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);

      if (lastUpdatedDate < tenDaysAgo) {
        stuckShipmentCount++;
      }
    }
  }

  const isStuck = stuckShipmentCount > 0;

  return { isStuck, stuckShipmentCount };
}

export function getCurrencyFormatterForReturn(
  return_: MerchantAppReturn,
  returnTotalsTypeToUse: ReturnTotalsType,
) {
  return getCurrencyFormatter(
    returnTotalsTypeToUse === ReturnTotalsType.NEW
      ? return_.presentmentCurrency
      : return_.currency,
  );
}

export function canEditReturnValue(
  hasEditReturnPermissions: boolean,
  return_: MerchantAppReturn,
  product?: ProductZod,
) {
  const isProductReturnable =
    !product ||
    (![ReturnedProductStatus.COMPLETE, ReturnedProductStatus.PENDING].includes(
      product.status,
    ) &&
      product.strategy !== "repair");

  return (
    hasEditReturnPermissions &&
    isProductReturnable &&
    !isClosedReturnStatus(return_.status)
  );
}

function getUpsellAmountCollected(return_: MerchantAppReturn): number {
  return (return_.paymentIntents ?? [])
    .filter((paymentIntent) => paymentIntent.type === "upsell")
    .reduce(
      (sum, paymentIntent) =>
        sum + (paymentIntent.payment_intent?.amount ?? 0) / 100,
      0,
    );
}

// The "can't collect more" warning only applies when payment is taken via Stripe
export function collectsUpsellViaStripe(return_: MerchantAppReturn): boolean {
  return return_.upsellBillingTrigger === UpsellBillingTrigger.ReturnCreated;
}

export function editWouldChargeCustomerMore(
  return_: MerchantAppReturn,
  returnTotals: SerializedReturnSummaryTotals,
): boolean {
  if (!collectsUpsellViaStripe(return_)) {
    return false;
  }
  const collected = getUpsellAmountCollected(return_);
  const outstanding =
    returnTotals.outstandingBalanceTotals?.withDraftBilledShipping ??
    returnTotals.outstandingBalance ??
    0;
  // A penny of float tolerance so an unchanged return never warns.
  return outstanding - collected > 0.01;
}

export function exchangeEditMayExceedCollected(
  return_: MerchantAppReturn,
  returnTotals: SerializedReturnSummaryTotals,
): boolean {
  return (
    collectsUpsellViaStripe(return_) &&
    (returnTotals.newOrderTotals?.totalValue ?? 0) > 0
  );
}
